import { useLayoutEffect } from 'react'

import { ensureFilterMenuPortalRoots } from 'components/cui_filter_menu/filter_menu_portals'
import { getMeetingsFilterMenuPortalRoot } from 'components/meetings/filter_modal/filter_menu_portal_root'
import { PORTALED_MENU_Z_INDEX } from 'components/meetings/filter_modal/portaled_menu_styles'
import { scrubFilterMenuPortalAriaHidden } from 'hooks/use_filter_menu_portal_focus_allow'

const SCOPE_MENU_COMPONENT = 'scope-dropdown-menu'
const SCOPE_OVERLAY_DATA_ATTR = 'meetingsFilterScopeOverlay'
const ELEVATED_Z_INDEX = String(PORTALED_MENU_Z_INDEX + 1)

const isScopeMenuElement = (node) => (
  node instanceof HTMLElement && node.dataset.component === SCOPE_MENU_COMPONENT
)

const popoverContainsScopeMenu = (popover) => {
  const stack = [popover]

  while (stack.length > 0) {
    const current = stack.pop()
    if (current instanceof HTMLElement) {
      if (isScopeMenuElement(current)) return true

      current.childNodes.forEach((child) => {
        if (child instanceof HTMLElement) {
          stack.push(child)
        }
      })
    }
  }

  return false
}

const getBodyPortaledRoot = (node) => {
  if (!(node instanceof HTMLElement)) return null

  let current = node
  while (current.parentElement && current.parentElement !== document.body) {
    current = current.parentElement
  }

  return current.parentElement === document.body ? current : null
}

const findScopeOverlayRoot = (node) => {
  if (!(node instanceof HTMLElement)) return null

  if (isScopeMenuElement(node)) {
    return getBodyPortaledRoot(node)
  }

  if (node.classList.contains('react-aria-Popover') && popoverContainsScopeMenu(node)) {
    return node
  }

  if (popoverContainsScopeMenu(node)) {
    return getBodyPortaledRoot(node)
  }

  return null
}

const elevateScopeOverlayRoot = (portalRoot, overlayRoot) => {
  overlayRoot.dataset[SCOPE_OVERLAY_DATA_ATTR] = 'true'
  overlayRoot.style.zIndex = ELEVATED_Z_INDEX
  scrubFilterMenuPortalAriaHidden(overlayRoot)

  if (
    overlayRoot.classList.contains('react-aria-Popover')
    && portalRoot
    && overlayRoot.parentElement !== portalRoot
  ) {
    portalRoot.appendChild(overlayRoot)
  }
}

const collectScopeOverlayRootsFromMutations = (mutations) => {
  const overlayRoots = new Set()

  mutations.forEach(({ addedNodes }) => {
    addedNodes.forEach((node) => {
      const overlayRoot = findScopeOverlayRoot(node)
      if (overlayRoot) overlayRoots.add(overlayRoot)
    })
  })

  return overlayRoots
}

/**
 * Lifts scope Select overlays above the full-screen meetings filter sheet.
 *
 * Phone Select uses CUI MobileOptions, which portals a FixedContainer to document.body at
 * z-index 15 — below the filter sheet (~400). Tablet/desktop use react-aria Popover.
 */
export const useMeetingsFilterScopePopoverPortal = (enabled) => {
  useLayoutEffect(() => {
    if (!enabled) return undefined

    ensureFilterMenuPortalRoots()
    const portalRoot = getMeetingsFilterMenuPortalRoot()
    if (!portalRoot) return undefined

    const syncFromMutations = (mutations) => {
      collectScopeOverlayRootsFromMutations(mutations).forEach((overlayRoot) => {
        elevateScopeOverlayRoot(portalRoot, overlayRoot)
      })
    }

    const observer = new MutationObserver(syncFromMutations)
    observer.observe(document.body, { childList: true, subtree: true })

    return () => {
      observer.disconnect()
    }
  }, [enabled])
}
